import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { toast } from "sonner";
import { copyTextReliable } from "@/lib/clipboard";
import {
  Newspaper, MapPin, CalendarDays, ArrowLeft, ArrowRight, ExternalLink, Zap,
  Copy, Check, Link2,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";
import FbGroupShare from "@/components/FbGroupShare";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import FeaturedPartners from "@/components/FeaturedPartners";
import PressOffers from "@/components/PressOffers";
import PressInterstitial from "@/components/PressInterstitial";
import EventBrandMentions from "@/components/EventBrandMentions";

export default function RevuePresse() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  // Pools pour les petits encarts insérés entre chaque section (offre + partenaire réels)
  const [interOffers, setInterOffers] = useState([]);
  const [interPartners, setInterPartners] = useState([]);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/revue-presse/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    let alive = true;
    api.get("/featured-partners?limit=12")
      .then((r) => { if (alive) setInterPartners(r.data?.partners || []); })
      .catch(() => {});
    api.get("/offers?status=published&limit=40")
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data.slice() : [];
        // Mélange de Fisher-Yates pour varier les offres à chaque chargement
        for (let i = list.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [list[i], list[j]] = [list[j], list[i]];
        }
        setInterOffers(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  useSeo({
    title: post ? `${post.title} | PROXIreduc-28` : "Revue de presse | PROXIreduc-28",
    description: post ? (post.meta_description || post.excerpt || post.title) : "",
    canonicalPath: `/revue-presse/${slug}`,
    image: (post && post.cover_image) ? post.cover_image : "/og-blog.jpg",
    keywords: post ? (post.keywords || []).join(", ") : undefined,
    type: "article",
  });

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="revue-404">
      <h1 className="font-heading text-2xl font-bold">Revue de presse introuvable</h1>
      <Link to="/revues-de-presse" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold"><ArrowLeft size={16} /> Toutes les revues</Link>
    </div>
  );
  if (!post) return <div className="max-w-2xl mx-auto px-4 py-20 text-slate-500">Chargement…</div>;

  const vitrineUrl = `${process.env.REACT_APP_BACKEND_URL}/api/revue-vitrine/${slug}`;
  const shareText = post.share_text || "";
  const track = (ch) => { api.post(`/revue-presse/${slug}/share`, { channel: ch }).catch(() => {}); };

  const copyShareText = () => {
    const ok = copyTextReliable(shareText);
    if (ok) { setCopiedText(true); setTimeout(() => setCopiedText(false), 2500); track("copy_text"); toast.success("Texte copié ! Vous pouvez maintenant le partager."); }
    else toast.error("Copie impossible — sélectionnez le texte manuellement.");
  };
  const copyLink = () => {
    const ok = copyTextReliable(vitrineUrl);
    if (ok) { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2500); track("copy_link"); toast.success("Lien de la revue copié !"); }
    else toast.error("Copie impossible.");
  };

  // Encarts inter-sections priorisés par la ville dominante de chaque section (repli départemental)
  const _norm = (s) => (s || "").trim().toLowerCase();
  const dominantCity = (articles) => {
    const counts = {};
    (articles || []).forEach((a) => { if (a.city) counts[a.city] = (counts[a.city] || 0) + 1; });
    let best = null, max = 0;
    Object.entries(counts).forEach(([c, n]) => { if (n > max) { max = n; best = c; } });
    return best;
  };
  const pickFor = (pool, city, used) => {
    if (city) {
      const i = pool.findIndex((x, idx) => !used.has(idx) && _norm(x.city) === _norm(city));
      if (i !== -1) { used.add(i); return pool[i]; }
    }
    const j = pool.findIndex((x, idx) => !used.has(idx));
    if (j !== -1) { used.add(j); return pool[j]; }
    return pool.length ? pool[0] : null;
  };
  const sectionPicks = (() => {
    const usedO = new Set(), usedP = new Set();
    return (post.sections_data || []).map((sec) => {
      const city = dominantCity(sec.articles);
      return { off: pickFor(interOffers, city, usedO), par: pickFor(interPartners, city, usedP) };
    });
  })();

  return (
    <article className="pb-24 sm:pb-16" data-testid="revue-detail">
      {post.cover_image && (
        <div className="w-full aspect-[2.4/1] max-h-[460px] bg-slate-100 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" data-testid="revue-cover" />
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/revues-de-presse" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638] bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3.5 py-2 rounded-full transition-colors" data-testid="revue-back-top"><ArrowLeft size={15} /> Toutes les revues</Link>

        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-[#172A3A] text-white font-semibold">Revue de presse</span>
          {post.digest_date && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(post.digest_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>}
          {post.article_count > 0 && <span className="inline-flex items-center gap-1"><Newspaper size={12} /> {post.article_count} articles du jour</span>}
        </div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="revue-title">{post.title}</h1>
        {post.intro ? <p className="mt-3 text-lg text-slate-600">{post.intro}</p>
          : post.excerpt && <p className="mt-3 text-lg text-slate-600">{post.excerpt}</p>}

        {/* Boutons de partage + copie */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="revue-share-box">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-slate-700 mr-1">Partager la revue</span>
            <ShareButton url={vitrineUrl} title={post.title} text={shareText} testId="revue-share" onShare={track} />
            <button onClick={copyLink} data-testid="revue-copy-link"
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium transition-colors ${copiedLink ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              {copiedLink ? <Check size={14} /> : <Link2 size={14} />} {copiedLink ? "Lien copié" : "Copier le lien"}
            </button>
          </div>

          {shareText && (
            <div className="mt-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Texte de partage — réseaux sociaux</div>
              <textarea data-testid="revue-share-textarea" value={shareText} readOnly rows={4}
                className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-2.5 resize-y focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" />
              <button onClick={copyShareText} data-testid="revue-copy-text"
                className={`mt-2 w-full sm:w-auto flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-bold border-2 transition-colors ${copiedText ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-[#E05638] border-[#E05638] text-white hover:bg-[#c94a2f]"}`}>
                {copiedText ? <Check size={16} /> : <Copy size={16} />} {copiedText ? "Texte copié !" : "Copier le texte de partage"}
              </button>
            </div>
          )}
        </div>

        {/* À la une */}
        {post.highlights_data?.length > 0 && (
          <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50/60 p-5 sm:p-6" data-testid="revue-highlights">
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#E05638] uppercase tracking-wide">
              <Zap size={16} /> À la une aujourd'hui
            </div>
            <div className="mt-4 space-y-4">
              {post.highlights_data.map((h, i) => (
                <div key={i} data-testid={`revue-highlight-${i}`} className="border-l-4 border-[#E05638] pl-4">
                  <h3 className="font-heading text-lg font-bold text-slate-900">
                    {h.source_url
                      ? <a href={h.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#E05638] transition-colors">{h.title}</a>
                      : h.title}
                  </h3>
                  {h.summary && <p className="mt-1 text-sm text-slate-600">{h.summary}</p>}
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-xs">
                    {h.city && <span className="inline-flex items-center gap-1 text-slate-500"><MapPin size={12} /> {h.city}</span>}
                    {h.source_url
                      ? <a href={h.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#E05638] hover:underline">Lire l'article sur {h.source_name || "le média"} <ExternalLink size={11} /></a>
                      : (h.source_name && <span className="inline-flex items-center gap-1 text-slate-500">Source : {h.source_name}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Encart partenaires discret — au milieu de l'article */}
        <FeaturedPartners limit={5} testId="revue-featured-partners" track />

        {/* Sections thématiques */}
        {post.sections_data?.map((sec, si) => {
          // Encart inséré après la section (sauf la dernière), priorisé par ville dominante
          const { off, par } = sectionPicks[si] || {};
          const showInter = si < post.sections_data.length - 1 && (off || par);
          return (
          <div key={si}>
          <section className="mt-10" data-testid={`revue-section-${si}`}>
            <h2 className="font-heading text-2xl font-bold text-[#172A3A] pb-2 border-b-2 border-slate-100">{sec.theme}</h2>
            <div className="mt-4 space-y-5">
              {sec.articles.map((a, ai) => (
                <div key={ai} className="bg-white border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors" data-testid={`revue-article-${si}-${ai}`}>
                  <h3 className="font-heading text-base font-bold text-slate-900">
                    {a.source_url
                      ? <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#E05638] transition-colors">{a.title}</a>
                      : a.title}
                  </h3>
                  {a.summary && <p className="mt-1 text-sm text-slate-600">{a.summary}</p>}
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-slate-500">
                    {a.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {a.city}</span>}
                    {a.published_at_label && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {a.published_at_label}</span>}
                    {a.source_url
                      ? <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#E05638] hover:underline">Lire l'article sur {a.source_name || "le média"} <ExternalLink size={11} /></a>
                      : (a.source_name && <span className="inline-flex items-center gap-1 text-slate-500">Source : {a.source_name}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </section>
          {showInter && <PressInterstitial offer={off} partner={par} testId={`revue-interstitial-${si}`} />}
          </div>
          );
        })}

        {post.signature && (
          <p className="mt-10 text-center text-sm font-semibold text-slate-500 italic">{post.signature}</p>
        )}

        {/* Bons plans discrets — plus bas dans la page */}
        <PressOffers limit={4} testId="revue-press-offers" track />

        <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Revues de presse", path: "/revues-de-presse" }, { name: post.title }]} />

        {/* Partage dans les groupes Facebook */}
        <div className="mt-10 pt-6 border-t border-slate-100" data-testid="revue-fb-groups">
          <FbGroupShare small showTopCopy testId="revue-fb-group-share" text={shareText} shareUrl={vitrineUrl} onShared={() => track("fb_groups")} />
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-200 text-center">
          <div className="font-heading text-xl font-bold text-slate-900">Ne ratez plus rien de votre territoire</div>
          <p className="mt-1 text-sm text-slate-600">Rejoignez gratuitement le réseau des bons plans d'Eure-et-Loir.</p>
          <Link to="/inscription" className="mt-4 inline-flex items-center gap-1 btn-shine text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="revue-cta-signup">
            Créer mon compte gratuit <ArrowRight size={16} />
          </Link>
        </div>

        {/* Deux petits encarts de marque — tout en bas de la revue */}
        <div className="mt-10 pt-8 border-t border-slate-100" data-testid="revue-brand-mentions-wrap">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Partenaires</div>
          <EventBrandMentions testId="revue-brand-mentions" />
        </div>

        <div className="mt-10 pt-8 border-t border-slate-100 text-center">
          <Link to="/revues-de-presse" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E05638] hover:bg-[#c94a2f] px-5 py-2.5 rounded-xl transition-colors" data-testid="revue-back-bottom">
            <ArrowLeft size={16} /> Toutes les revues de presse
          </Link>
        </div>
      </div>
    </article>
  );
}
